import { useRef, useState } from "react";
import moment from "moment/moment";
import { FaLink } from "react-icons/fa6";
import { IoMdAdd } from "react-icons/io";
import Swal from "sweetalert2";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { useMutation, useQuery } from "@tanstack/react-query";

import useRole from "../../hooks/useRole";
import { acceptEvent, deleteEvent, generateCalendarLink, getEvents, getOpenEvents, syncEvents, updateEvent, updateEventStatus } from "../../services/services";
import { constant } from "../../utils/constants";
import { toastAlert } from "../../utils/SweetAlert";
import AddRequest from "../client-request/AddRequest";

const ClientCalendar = () => {
  const calendarRef = useRef(null);
  const clickTimer = useRef(null);
  const role = useRole();
  const [currentView, setCurrentView] = useState("");
  const [show, setShow] = useState(false);

  const [currentRange, setCurrentRange] = useState({
    start: null, 
    end: null
  });
  const eventStartDate = currentRange.start?.split('T')?.[0] ?? undefined
  const eventEndDate = currentRange.end?.split('T')?.[0] ?? undefined
  const range = eventStartDate && eventEndDate ? `${eventStartDate}TO${eventEndDate}` : undefined;
  
  const handleShow = () => {
    let showdata = true;
    if (currentView === "timeGridDay") {
      const currentDate = calendarRef.current.getApi().getDate();
      showdata = { date: currentDate };
    }
    setShow(showdata);
  };

  const getEventStyle = (eventObj, employeeDuration) => {
    let classname = "";
    let backgroundColor = "red";
    let textColor = "black";
    let isOverBooked = false;

    const startDate = new Date(eventObj?.startTime);
    const fullDate = startDate.toISOString().split("T")[0];
    const isCancelled = eventObj.status === constant.EVENT_STATUS.CANCELLED;
    const isDenied = eventObj.status === constant.EVENT_STATUS.REJECTED;
    const isAccepted = eventObj.status === constant.EVENT_STATUS.ACCEPTED;

    if (
      employeeDuration &&
      employeeDuration[fullDate] &&
      employeeDuration[fullDate][eventObj.employeeId] &&
      employeeDuration[fullDate][eventObj.employeeId] >=
      constant.MAX_DURATION_PER_EMPLOYEE
    ) {
      isOverBooked = true;
    }

    if (eventObj.locked === true && role === "employee") {
      classname = "locked_event";
      backgroundColor = "purple";
    } else if (isCancelled) {
      classname = "cancelled_event";
      backgroundColor = "blue";
    } else if (isDenied && role === "employee") {
      classname = "denied_event";
      backgroundColor = "brown";
    } else if (isDenied && role === "client") {
      classname = "unassigned_event";
      backgroundColor = "red";
    } else if (isAccepted || (eventObj?.status === "confirmed" && eventObj?.steps !== 2)) {
      classname = "scheduled_assigned_event";
      backgroundColor = "green";
    } else if (eventObj?.steps !== 2) {
      classname = "unassigned_event";
      backgroundColor = "red";
    } else if (isAccepted || eventObj?.status === "confirmed") {
      classname = "accepted_event";
      backgroundColor = "lightgrey";
    } else if (eventObj?.employeeId && eventObj?.status !== "confirmed" && role === "client") {
      classname = "unassigned_event";
      backgroundColor = "red";
    } else if (eventObj?.status === "pending" && role === "employee") {
      classname = "unassigned_event";
      backgroundColor = "red";
    } else if (!eventObj?.employeeId || eventObj?.employeeId === "") {
      classname = "unassigned_event";
      backgroundColor = "red";
    } else if (eventObj?.employeeId && !eventObj?.sync && isOverBooked) {
      classname = "accepted_event";
      backgroundColor = "red";
    } else if (eventObj?.employeeId && !eventObj?.sync) {
      classname = "notsync_event";
      backgroundColor = "yellow";
    } else if (eventObj?.employeeId && eventObj?.sync) {
      classname = "synced_assigned_event";
      backgroundColor = "green";
    } else if (isOverBooked) {
      classname = "accepted_event";
      backgroundColor = "red";
    }

    return { backgroundColor, className: classname, textColor };
  };

  const { data, refetch } = useQuery({
    queryKey: ["event-list", role, range],
    queryFn: async ({ signal }) => {
      let params = {
        sortedBy: constant.SORTED_BY.START_TIME,
        sortBy: constant.SORT_BY.ASC,
        range,
      };

      let resp;
      try {
        resp = await getEvents(params, signal);
      } catch (error) {
        console.error("Error fetching events:", error);
        resp = { data: { data: { events: [] } } };
      }

      let openEvents = [];
      if (role === "employee") {
        try {
          const resp1 = await getOpenEvents(params, signal);
          openEvents = resp1.data?.data?.events || [];
        } catch (error) {
          console.error("Error fetching open events:", error);
        }
      }

      const allEvents = [
        ...(resp.data?.data?.events || []),
        ...openEvents,
      ];
      const uniqueEvents = Array.from(
        new Map(allEvents.map((event) => [event._id, event])).values()
      );

      let calendarEvents = [];

      if (uniqueEvents.length) {
        let employeeDuration = {};
        calendarEvents = uniqueEvents.map((event) => {
          const clientDetails = event?.clientDetails || null;
          const employeeDetails = event?.employeeDetails || null;
          const startDate = new Date(event?.startTime);
          const fullDate = startDate.toISOString().split("T")[0];
          if (event.employeeId && event.employeeId !== "") {
            if (employeeDuration[fullDate]) {
              const innerObject = { ...employeeDuration[fullDate] };
              if (innerObject[event.employeeId]) {
                innerObject[event.employeeId] += event.duration;
              } else {
                innerObject[event.employeeId] = event.duration;
              }
              employeeDuration[fullDate] = innerObject;
            } else {
              employeeDuration[fullDate] = {
                [event.employeeId]: event.duration,
              };
            }
          }

          const styleProperies = getEventStyle(event, employeeDuration);

          const note = event?.note?.trim() || "N/A";
          const shortNote =
            note.length > 50 ? `${note.slice(0, 50)}...` : note;

          const employeeName =
            event.employeeId &&
            ["confirmed", "accepted", "cancelled"].includes(event.status)
              ? `${employeeDetails?.firstName || ""} ${
                  employeeDetails?.lastName || ""
                }${event.status === "confirmed" ? " (Confirmed)" : ""}`
              : "N/A";
              
          const clientName = event.clientId
            ? `${clientDetails?.firstName || ""} ${
                clientDetails?.lastName || ""
              }`
            : "N/A";
            
          const fullTitle =
            role === "employee"
              ? `Client: ${clientName}
Time: ${moment(event?.startTime).format("LT")} - ${moment(
                  event?.endTime
                ).format("LT")}
Address: ${clientDetails?.userAddress?.address || "N/A"}`
              : `Employee: ${employeeName}
Time: ${moment(event?.startTime).format("LT")} - ${moment(
                  event?.endTime
                ).format("LT")}
Notes: ${note}`;
              
          const displayTitle =
            role === "employee"
              ? fullTitle
              : `Employee: ${employeeName}
Time: ${moment(event?.startTime).format("LT")} - ${moment(
                  event?.endTime
                ).format("LT")}
${currentView === "timeGridWeek" || currentView === "timeGridDay" ? `Notes: ${shortNote}`:""}`;
              
          return {
            start: new Date(event?.startTime),
            end: new Date(event?.endTime),
          
            title: displayTitle,
            description: note,
          
            extendedProps: {
              ...event,
              client: clientDetails,
              employee: employeeDetails,
              fullTitle,
            },
            ...styleProperies,
          };

          // return {
          //   start: new Date(event?.startTime),
          //   end: new Date(event?.endTime),
          //   title:
          //     role === "employee"
          //       ? `Client: ${event.clientId
          //           ? `${clientDetails?.firstName || ""} ${clientDetails?.lastName ?? ""}`
          //           : "N/A"
          //         }\nTime: ${moment(event?.startTime).format("LT")} - ${moment(event?.endTime).format("LT")}\nAddress: ${clientDetails?.userAddress?.address || "N/A"}`
          //       : `Employee: ${(event.employeeId && (event.status === "confirmed" || event.status === "accepted" || event.status === "cancelled"))
          //           ? `${employeeDetails?.firstName || ""} ${employeeDetails?.lastName ?? ""}${(event.status === "confirmed") ? " (Confirmed)" : ""}`
          //           : "N/A"
          //         }\nTime: ${moment(event?.startTime).format("LT")} - ${moment(event?.endTime).format("LT")}\nNotes:${event?.note?.slice(0, 30) || " N/A"}`,
          //   description: event?.note,
          //   extendedProps: {
          //     ...event,
          //     client: clientDetails,
          //     employee: employeeDetails,
          //   },
          //   ...styleProperies,
          // };
        });
      }
      return calendarEvents;
    },
    staleTime: 1000 * 60, // stale time 1 min - the time for which the data is considered fresh and will not be refetched
  });

  const handleDatesSet = (dateInfo) => {
    // Save the new string representations of the range to state
    setCurrentRange({
      start: dateInfo.startStr,
      end: dateInfo.endStr,
    });
    setCurrentView(dateInfo.view.type);
  };

  const syncEventsMutation = useMutation({
    mutationFn: () => syncEvents(),
    onSuccess: async (resp) => {
      if (resp.data.statusCode === constant.STATUS_CODE.SUCCESS) {
        toastAlert("success", resp.data?.message);
      }
      refetch();
    },
  });

  const acceptMutation = useMutation({
    mutationFn: async (event) => {
      try {
        const response = await acceptEvent({
          id: event._id,
          accept: true,
        });
        console.log("Accept Success:", response);
        return response;
      } catch (error) {
        error.event = event;
        console.error("Accept Mutation Error:", error);
        throw error;
      }
    },
    onSuccess: (resp) => {
      toastAlert("success", "Assignment accepted successfully");
      refetch();
    },
    onError: (error) => {
      if (error.response?.data?.message === "You have another event assigned") {
        Swal.fire({
          title: "Cannot Accept Assignment",
          text: "There is a scheduling conflict for this assignment, Please contact your manager to accept this assignment",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#d33",
        });
      } else {
        Swal.fire({
          title: error.conflictEvent ? "Time Conflict Detected" : "Cannot Accept Assignment",
          html: error.conflictEvent
            ? `
                <p>This assignment conflicts with your existing schedule:</p>
                <p><strong>${moment(error.conflictEvent.startTime).format("MMM D, YYYY h:mm A")}</strong> to 
                <strong>${moment(error.conflictEvent.endTime).format("MMM D, YYYY h:mm A")}</strong></p>
                <p>Would you like to deny this assignment instead?</p>
              `
            : `
                <p>Could not accept assignment due to:</p>
                <p><strong>${error.response?.data?.message || error.message || "Unknown error"}</strong></p>
                <p>Would you like to deny this assignment instead?</p>
              `,
          icon: "error",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#6c757d",
          confirmButtonText: "Deny Assignment",
          cancelButtonText: "Cancel",
        }).then((result) => {
          if (result.isConfirmed && error.event) {
            denyMutation.mutate(error.event);
          }
        });
      }
    },
  });

  const denyMutation = useMutation({
    mutationFn: async (event) => {
      await acceptEvent({
        id: event._id,
        accept: false,
      });
    },
    onSuccess: () => {
      toastAlert("success", "Assignment denied successfully");
      refetch();
    },
  });

  const eventMutation = useMutation({
    mutationFn: ({ id, status }) => updateEventStatus(id, status),
    onSuccess: (response) => {
      toastAlert("success", response.data?.message || "Status updated successfully");
      refetch();
    },
  });

  const mutation = useMutation({
    mutationFn: (body) => updateEvent(body.data),
    onSuccess: (resp) => {
      toastAlert("success", resp?.data?.message || "Event updated successfully");
      refetch();
    },
    onError: (error, arg) => {
      toastAlert("error", error.response?.data?.message || "Failed to update event");
      arg.revert();
    },
  });

  const handleDragging = ({ event, revert }) => {
    const { status, employeeId, locked, startTime } = event?.extendedProps;
    const currentTime = moment();

    // 1. Prevent drag-and-drop for cancelled events
    if (status === constant.EVENT_STATUS.CANCELLED) {
      toastAlert("warning", "Cannot update cancelled events");
      revert();
      return;
    }

    // 2. Prevent editing past events
    const eventStartTime = moment(startTime);
    if (eventStartTime.isBefore(currentTime)) {
      toastAlert("warning", "Cannot update past events");
      revert();
      return;
    }

    // 3. Check if event is eligible for update
    const isEligibleForUpdate = (
      (status === "pending" && employeeId == null) ||  // Case 1: pending + no employee → always eligible
      (
        status !== "accepted" && 
        status !== "confirmed" && 
        status !== "rejected" && 
        employeeId == null &&
        !(locked && role === "client")
      )  // Case 2: other allowed statuses + additional checks
    );

    if (!isEligibleForUpdate) {
      toastAlert("warning", "Cannot update this event. Please contact admin.");
      revert();
      return;
    }

    // 4. Prevent date changes (same-day updates only)
    const updatedEnd = moment(event?.end);
    const updatedStart = moment(event?.start);
    const originalStart = moment(startTime);
    
    if (!originalStart.isValid() || !updatedStart.isSame(originalStart, "day")) {
      revert();
      toastAlert("warning", "Cannot change date of the event");
      return;
    }

    // Prepare update payload
    const update = {
      override: false,
      id: event?.extendedProps?._id,
      clientId: event?.extendedProps.clientId,
      employeeId: event?.extendedProps.employeeId || null,
      note: event?.extendedProps?.note || "",
      startTime: updatedStart.format(constant.DATE_FORMAT),
      endTime: updatedEnd.format(constant.DATE_FORMAT),
      cancelEvent: event?.extendedProps?.status === "cancelled" ? true : false,
      locked: event?.extendedProps?.locked || false,
    };

    mutation.mutate({ data: update, revert });
  };

  const handleDateClick = (info) => {
    const clickedDate = new Date(info.date);
    const today = new Date();
    if (clickedDate >= today.setHours(0, 0, 0, 0)) {
      if (currentView === "timeGridDay" || currentView === "timeGridWeek") {
        if (role !== constant.ROLE.CLIENT) return;
        const today = new Date();
        if (clickedDate >= today) setShow(info);
      } else {
        const selectedDate = info.dateStr;
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.changeView("timeGridDay", selectedDate);
        }
      }
    } else console.log("Past date selected");
  };

const isTodayOrTomorrow = (eventDate) => {
  const eventMoment = moment(eventDate);
  return (
    eventMoment.isSame(moment(), 'day') ||                     // today
    eventMoment.isSame(moment().add(1, 'day'), 'day')          // tomorrow
  );
};


  const handleAcceptEvent = (event) => {
    Swal.fire({
      title: "Confirm Acceptance",
      text: "Are you sure you want to accept this assignment?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Accept",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3085d6",
    }).then((result) => {
      if (result.isConfirmed) {
        if (event.locked === true) {
          acceptMutation.mutate(event);
        } else {
          eventMutation.mutate({
            id: event._id,
            status: constant.EVENT_STATUS.ACCEPTED,
          });
        }
      }
    });
  };

  const handleDenyEvent = (event) => {
    Swal.fire({
      title: "Confirm Denial",
      text: "Are you sure you want to deny this assignment?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Deny",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    }).then((result) => {
      if (result.isConfirmed) {
        if (event.locked === true) {
          denyMutation.mutate(event);
        } else {
          eventMutation.mutate({
            id: event._id,
            status: constant.EVENT_STATUS.REJECTED,
          });
        }
      }
    });
  };

  const handleConfirmEvent = (event) => {
    Swal.fire({
      title: "Confirm Assignment",
      text: "Are you sure you want to confirm this assignment?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Confirm",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#28a745",
    }).then((result) => {
      if (result.isConfirmed) {
        eventMutation.mutate({
          id: event._id,
          status: constant.EVENT_STATUS.CONFIRMED,
        });
      }
    });
  };

  const handleEmployeeEventClick = (eventData) => {
    console.log("Event data:", eventData);

    if (eventData?.status === "pending") {
      const swalConfig = {
        title: "Event Assignment",
        html: `
        <div>
          <p>Please choose an action for this assignment</p>
        </div>`,
        icon: "question",
        showCancelButton: false,
        showCloseButton: true,
        reverseButtons: false,
        confirmButtonColor: "#3085d6",
        denyButtonColor: "#d33",
        customClass: {
          actions: "swal-order",
          confirmButton: "swal-accept-btn",
          denyButton: "swal-deny-btn",
        },
        didOpen: () => {
          const actionsContainer = document.querySelector(".swal2-actions");
          if (actionsContainer && isTodayOrTomorrow(eventData.startTime)&&!eventData.locked) {
            const confirmBtn = document.createElement("button");
            confirmBtn.innerText = "Confirm";
            confirmBtn.className = "swal2-styled swal-confirm-btn";
            confirmBtn.style.backgroundColor = "#28a745";
            confirmBtn.style.color = "white";
            const denyBtn = document.querySelector(".swal2-deny");
            if (denyBtn) {
              denyBtn.insertAdjacentElement("afterend", confirmBtn);
            } else {
              actionsContainer.appendChild(confirmBtn);
            }

            confirmBtn.addEventListener("click", async () => {
              try {
                await handleConfirmEvent(eventData);
                swalInstance.close();
              } catch (error) {}
            });
          }
        },
      };

      if (eventData.locked === true) {
        swalConfig.showDenyButton = false;
        swalConfig.showConfirmButton = true;
        swalConfig.confirmButtonText = "Accept";
      } else {
        swalConfig.showDenyButton = true;
        swalConfig.showConfirmButton = true;
        swalConfig.confirmButtonText = "Accept";
        swalConfig.denyButtonText = "Deny";
      }

      const swalInstance = Swal.fire(swalConfig).then((result) => {
        if (result.isConfirmed) {
          handleAcceptEvent(eventData);
        } else if (result.isDenied) {
          handleDenyEvent(eventData);
        }
      });
    }
  };

  const handleCancel = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You are going to cancel this event!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00f",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, cancel it!",
    }).then((result) => {
      if (result.isConfirmed) cancelMutation.mutate(id);
    });
  };

  const cancelMutation = useMutation({
    mutationFn: async (id) => {
      return await updateEventStatus(id, constant.EVENT_STATUS.CANCELLED);
    },
    onSuccess: () => {
      toastAlert("success", "Event has been cancelled");
      refetch();
    },
  });

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) deleteMutation.mutate(id);
    });
  };

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return await deleteEvent(id);
    },
    onSuccess: () => {
      toastAlert("success", "Event has been deleted");
      refetch();
    },
  });

  const handleEventClick = (arg) => {
    clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => {
      let id = arg?.event?.extendedProps?._id;
      let status = arg?.event?.extendedProps?.status;

      if (status === "accepted" || status === "confirmed") {
        Swal.fire({
          icon: "warning",
          title: "Actions",
          text: "You won't be able to revert this!",
          showDenyButton: true,
          showCloseButton: true,
          denyButtonText: "Cancel Event",
          denyButtonColor: "#00f",
          customClass: {
            actions: "swal-order",
            denyButton: "swal-cancel-btn",
          },
        }).then((result) => {
          if (result.isDenied) {
            handleCancel(id);
          }
        });
      } else {
        Swal.fire({
          icon: "warning",
          title: "Actions",
          text: "You won't be able to revert this!",
          showCancelButton: true,
          showConfirmButton: true,
          showDenyButton: true,
          confirmButtonText: "Delete Event",
          cancelButtonText: "Edit Event",
          denyButtonText: "Cancel Event",
          showCloseButton: true,
          reverseButtons: true,
          confirmButtonColor: "#dc3545",
          denyButtonColor: "#00f",
          customClass: {
            actions: "swal-order",
            cancelButton: "swal-edit-btn",
            confirmButton: "swal-delete-btn",
            denyButton: "swal-cancel-btn",
          },
        }).then((result) => {
          if (result.isConfirmed) {
            handleDelete(id);
          } else if (result.isDenied) {
            handleCancel(id);
          } else if (result.isDismissed && result.dismiss === Swal.DismissReason.cancel) {
            handleEventDoubleClick(arg);
          }
        });
      }
    }, 300);
  };

  const handleEventClick1 = (arg) => {
    clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => {
      Swal.fire({
        icon: "warning",
        title: "Actions",
        text: "Please contact admin to delete or cancel this event!",
        showCloseButton: false,
        confirmButtonText: "OK",
        allowOutsideClick: false,
        confirmButtonColor: "#0d1227",
      }).then((result) => {
        if (result.isConfirmed) {
        }
      });
    }, 300);
  };

  const handleEventDoubleClick = (arg) => {
    clearTimeout(clickTimer.current);
    const clickedEventDate = new Date(arg.event.start);
    setShow(arg);
  };

  const generateLink = useMutation({
    mutationFn: generateCalendarLink,
    onSuccess: async (resp) => {
      const link = resp.data.data;
      showCalendarLinkPopup(link);
    },
  });

  const showCalendarLinkPopup = (link) => {
    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(link);
        Swal.fire({
          icon: "success",
          title: "Copied!",
          text: "The link has been copied to your clipboard.",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    };

    Swal.fire({
      title: "Generated Calendar Link",
      html: `
        <input type="text" id="calendarLink" value="${link}" readonly 
          style="width: 100%; padding: 5px; text-align: center; border: 1px solid #ccc; border-radius: 5px;">
        <button id="copyButton" style="margin-top: 10px; padding: 5px 10px; background: #0d1227; color: white; border: none; border-radius: 5px; cursor: pointer;">
          Copy Link
        </button>
      `,
      showConfirmButton: false,
      didOpen: () => {
        document.getElementById("copyButton").addEventListener("click", copyToClipboard);
      },
    });
  };

  return (
    <div className="main-content">
      <style>
        {`
          .swal-lock-btn, .swal-confirm-btn {
            border-radius: 4px;
            border: none;
            font-size: 16px;
            cursor: pointer;
            order: 4;
          }
          .fc-event {
            pointer-events: auto;
          }
          .fc-event-dot,
          .fc-event-title > * {
            pointer-events: none;
          }
          .fc-daygrid .fc-event-title {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            flex: 1;
            min-width: 0;
          }
        `}
      </style>
      <div className="commonSearchBar my-3">
        <h4>Calendar</h4>
        <div className="endContent">
          <button
            className="greenBtn"
            type="button"
            title="Calendar Link"
            onClick={generateLink.mutate}
          >
            <FaLink size={30} />
          </button>
          {role === constant.ROLE.CLIENT && (
            <button
              className="greenBtn calendar-btn"
              type="button"
              title="Add Event"
              onClick={handleShow}
            >
              <IoMdAdd size={35} />
            </button>
          )}
        </div>
      </div>
      <FullCalendar
        ref={calendarRef}
        datesSet={handleDatesSet}
        plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin]}
        events={data}
        editable={role === "client"}
        eventResizableFromStart={role === "client"}
        eventResize={role === "client" ? handleDragging : undefined}
        eventDrop={role === "client" ? handleDragging : undefined}
        dayMaxEventRows={true}
        nowIndicator={true}
        initialView="dayGridMonth"
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
        }}
        eventClick={(el) => {
          const clickedEventDate = new Date(el.event.start);
          const eventData = el.event.extendedProps;

          if (role === "employee") {
            handleEmployeeEventClick(eventData);
            return;
          }

          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (clickedEventDate >= today) {
            const { status, employeeId } = el.event.extendedProps;
            if (status === "rejected" || employeeId == null || status === "accepted" || status === "confirmed") {
              handleEventClick(el);
            } else {
              handleEventClick1(el);
            }
          }
        }}
        eventDidMount={(info) => {
          info.el.setAttribute("title", info.event.extendedProps.fullTitle || info.event.title);
          info.el.addEventListener("dblclick", () => {
            if (role === "client") {
              const { status, employeeId } = info.event.extendedProps;
              if (status === "rejected" || employeeId == null) {
                handleEventDoubleClick(info);
              }
            }
          });
          info.el.addEventListener("click", (e) => {
            e.stopPropagation();
            const clickedEventDate = new Date(info.event.start);
            const eventData = info.event.extendedProps;
            if (role === "employee") {
              handleEmployeeEventClick(eventData);
              return;
            }
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (clickedEventDate >= today) {
              const { status, employeeId } = info.event.extendedProps;
              if (status === "rejected" || employeeId == null || status === "accepted" || status === "confirmed") {
                handleEventClick(info);
              } else {
                handleEventClick1(info);
              }
            }
          });
        }}
        eventContent={(eventInfo) => {
          const isLocked = eventInfo.event.extendedProps?.locked;
          const isCancelled =
            eventInfo.event.extendedProps?.status ===
            constant.EVENT_STATUS.CANCELLED;
        
          const forceWhiteText =
            (eventInfo.view.type === "timeGridWeek" ||
              eventInfo.view.type === "timeGridDay") &&
            (isLocked || isCancelled);
            
          const isMonthView = eventInfo.view.type === "dayGridMonth";
            
          let eventTitle = eventInfo.event.title
            .replace(/\n/g, "<br/>")
            .replace(
              /\(Confirmed\)/gi,
              '<span style="color:green;">(Confirmed)</span>'
            )
            .replace(
              /\(OT\)/g,
              '<span style="color:red;">(OT)</span>'
            );
          
          return (
            <div
              className="fc-event"
              style={{
                display: "flex",
                alignItems: "flex-start",
                width: "100%",
              }}
            >
              {isMonthView && (
                <span
                  className="fc-event-dot"
                  style={{
                    backgroundColor:
                      eventInfo.event.backgroundColor || "red",
                    borderRadius: "50%",
                    width: "8px",
                    height: "8px",
                    marginRight: "5px",
                    marginTop: "5px",
                    flexShrink: 0,
                  }}
                />
              )}
        
              <div
                className="fc-event-title"
                style={{
                  whiteSpace: "normal",
                  lineHeight: "1.3",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <b
                  style={{
                    color: forceWhiteText ? "white" : "inherit",
                  }}
                >
                  {eventInfo.timeText}
                </b>
                
                <span
                  style={{
                    color: forceWhiteText ? "white" : "inherit",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: `<br/>${eventTitle}`,
                  }}
                />
              </div>
            </div>
          );
        }}
        // eventContent={(eventInfo) => {
        //   const isLocked = eventInfo.event.extendedProps?.locked;
        //   const isCancelled = eventInfo.event.extendedProps?.status === constant.EVENT_STATUS.CANCELLED;
        //   const forceWhiteText =
        //     (eventInfo.view.type === "timeGridWeek" ||
        //       eventInfo.view.type === "timeGridDay") &&
        //     (isLocked || isCancelled);
        //   const isMonthView = eventInfo.view.type === "dayGridMonth";
        //   let eventTitle = eventInfo.event.title;

        //   // Style "Confirmed" and "OT" in green and red
        //   eventTitle = eventTitle.replace(
        //     /\(Confirmed\)/gi,
        //     '<span style="color: green;">(Confirmed)</span>'
        //   );
        //   eventTitle = eventTitle.replace(
        //     /\(OT\)/g,
        //     '<span style="color: red;">(OT)</span>'
        //   );

        //   return (
        //     <div
        //       className="fc-event"
        //       style={{
        //         display: "flex",
        //         alignItems: "center",
        //         width: "100%",
        //       }}
        //     >
        //       {isMonthView && (
        //         <span
        //           className="fc-event-dot"
        //           style={{
        //             backgroundColor: eventInfo.event.backgroundColor || "red",
        //             borderRadius: "50%",
        //             display: "inline-block",
        //             width: "8px",
        //             height: "8px",
        //             marginRight: "5px",
        //             flexShrink: 0,
        //           }}
        //         ></span>
        //       )}
        //       <div
        //         className="fc-event-title"
        //         style={{
        //           whiteSpace: isMonthView ? "nowrap" : "normal",
        //           overflow: isMonthView ? "hidden" : "visible",
        //           textOverflow: isMonthView ? "ellipsis" : "clip",
        //           flex: 1,
        //           minWidth: 0,
        //         }}
        //       >
        //         <b style={{ color: forceWhiteText ? "white" : "inherit" }}>
        //           {eventInfo.timeText}
        //         </b>{" "}
        //         <span
        //           style={{ color: forceWhiteText ? "white" : "inherit" }}
        //           dangerouslySetInnerHTML={{ __html: eventTitle }}
        //         />
        //       </div>
        //     </div>
        //   );
        // }}
        dateClick={handleDateClick}
      />
      {show && <AddRequest show={show} setShow={setShow} refetch={refetch} />}
    </div>
  );
};

export default ClientCalendar;
